import { inject, Injectable } from '@angular/core';
import { app, App } from '@df/core/app';
import { SubscriptionLike } from 'rxjs';

/**
 * Event service usage
 *
 * Broadcasting
 *
 * 1. Define event interfaces
 *
 * import { DfEventConfig } from '@df/core/service/df-event';
 *
 * declare module '@df/core/service/df-event' {
 *      interface IEventType {
 *          someEventId: DfEventConfig<'someEventId', { ... data object interface ... }, { ... result data interface ... }>;
 *      }
 * }
 *
 * 2. Dispatch event
 *
 * Keep in mind that action may exist or not. It may be async or not.
 *
 * this.dfEvent.dispatch({
 *   id: 'someEventId',
 *   data: { ... data object ... },
 *   async action: (data) => {
 *      // do something
 *     return { ... result object ... };
 *    }
 * });
 *
 * 3. Capture event
 *
 * this.dfEvent.before(event => {
 *    if(event.id === 'someEventId') {
 *         console.log(event.data)
 *    }
 * })
 *
 * - You may stop processing by setting event.stopped = true;
 * - You may call event.action manually
 * - You may call event before, on success or on error
 * - You may kill the action only by making it undefined in event
 */

type ObserverMode = 'before' | 'success' | 'error';

interface Observer {
    mode: ObserverMode;
    cb: (event: DfEventData) => void | Promise<void>;
    priority: number;
}

type DfEventAction<TData, TResult> = (event: TData) => TResult | Promise<TResult>;

export interface DfEventConfig<TId, TData, TResult> {
    id: TId;
    data: TData;
    result?: TResult;
    error?: string;
    stopped?: boolean;
    action?: DfEventAction<TData, TResult>;
}

export interface IEventType {}

export type DfEventKey = keyof IEventType;
export type DfEventData = IEventType[DfEventKey];

/**
 * Event subscription
 */
class DfEventSubscription implements SubscriptionLike {
    constructor(
        private observer: Observer,
        private observers: Set<Observer>,
        private service: DfEventService
    ) {}

    unsubscribe(): void {
        this.service.unsubscribe(this.observer);
    }

    get closed() {
        return this.observers.has(this.observer);
    }
}

declare module '@df/core/app' {
    interface App {
        event: DfEventService;
    }
}

/**
 * Event service
 */
@Injectable({
    providedIn: 'root'
})
export class DfEventService {
    /**
     * List of the observince methods
     */
    private observers = new Set<Observer>();

    constructor() {
        inject(App).event = this;
    }

    /**
     * Subscrbe to before-action event
     */
    before(cb: Observer['cb'], config?: Partial<Omit<Observer, 'mode' | 'cb'>>) {
        return this.on(cb, 'before', config);
    }

    /**
     * Subscribe to after-action event
     */
    success(cb: Observer['cb'], config?: Partial<Omit<Observer, 'mode' | 'cb'>>) {
        return this.on(cb, 'success', config);
    }

    /**
     * Subscrbe to before-action event
     */
    error(cb: Observer['cb'], config?: Partial<Omit<Observer, 'mode' | 'cb'>>) {
        return this.on(cb, 'error', config);
    }

    /**
     * Dispatch event
     */
    async dispatch<TResult>(event: DfEventData): Promise<TResult> {
        await this.trigger('before', event);
        if (!event.stopped) {
            if (event.action) {
                try {
                    const actionResult = event.action(event.data as any);
                    event.result = actionResult instanceof Promise ? await actionResult : actionResult;
                } catch (err) {
                    if (err instanceof Error) {
                        event.error = err.message;
                    } else if (typeof err === 'object' && err !== null && 'error' in err && typeof err.error === 'string') {
                        event.error = err.error;
                    } else if (typeof err === 'string') {
                        event.error = err;
                    } else if (typeof err === 'number') {
                        event.error = err.toString();
                    } else {
                        event.error = 'ERROR';
                    }
                    const error = event.error;
                    await this.trigger('error', event);
                    if (event.error) {
                        throw error === event.error ? err : { error: event.error };
                    }
                    if (event.stopped) {
                        return event.result as TResult;
                    }
                }
            }
            await this.trigger('success', event);
        }
        return event.result as TResult;
    }

    /**
     * Remove observer from the list
     */
    unsubscribe(observer: Observer) {
        if (this.observers.has(observer)) {
            this.observers.delete(observer);
            const sequence = this.observersSequences[observer.mode];
            if (sequence) {
                const index = sequence.indexOf(observer);
                if (index >= 0) {
                    sequence.splice(index, 1);
                }
            }
        }
    }

    /**
     * Observers sequences
     * ordered and grouped by mode
     *
     * Sequence is created when needed
     */
    private observersSequences: Partial<Record<ObserverMode, Observer[]>> = {};

    /**
     * Add observer to the list
     */
    private on(cb: Observer['cb'], mode: ObserverMode, config?: Partial<Omit<Observer, 'mode' | 'cb'>>): SubscriptionLike {
        const observer: Observer = {
            mode,
            cb,
            priority: 100
        };
        if (config) {
            Object.assign(observer, config);
        }
        this.observers.add(observer);
        // invalidate existing sequence
        if (this.observersSequences[mode]) {
            delete this.observersSequences[mode];
        }

        return new DfEventSubscription(observer, this.observers, this);
    }

    /**
     * Trigger all observers
     */
    private async trigger(mode: ObserverMode, event: DfEventData) {
        for (const observer of this.getSequence(mode)) {
            const result = observer.cb(event);
            if (result instanceof Promise) {
                await result;
            }
        }
    }

    /**
     * Regenerate sequence if needed and return it
     */
    private getSequence(mode: ObserverMode): Observer[] {
        if (!this.observersSequences[mode]) {
            this.observersSequences[mode] = Array.from(this.observers)
                .filter(i => i.mode === mode)
                .sort((a, b) => a.priority - b.priority);
        }
        return this.observersSequences[mode]!;
    }
}
